/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Flex, Text, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CheckMarkIcon, DotIcon} from '../icons'
import {validatePassword} from '@salesforce/retail-react-app/app/utils/password-utils'

/**
 * UI component for styling password requirement line
 */
const PasswordRequirement = ({isValid, children}) => {
    return (
        <Flex align="center" gap="1">
            {isValid ? <CheckMarkIcon w="4" h="4" color="green.600" /> : <DotIcon w="4" h="4" color="black" />}
            <Text fontSize="xs" lineHeight="1" py="4px" color={isValid ? 'green.600' : 'black'}>
                {children}
            </Text>
        </Flex>
    )
}

PasswordRequirement.propTypes = {
    /** Should it render in valid state */
    isValid: PropTypes.bool,

    /** The requirement text */
    children: PropTypes.any
}

/**
 * Renders a list of password requirments. Each requirement line toggles to its `isValid`
 * state when the given password value meets the associated critieria.
 */
const STACK_SPACING = {
    confirmation: {mb: 0, mt: 0}
}

const PasswordRequirements = ({value, source}) => {
    const pwValidations = validatePassword(value)
    const hasInvalidRequirements = value && Object.values(pwValidations).some((isValid) => !isValid)
    const stackSpacing = STACK_SPACING[source] ?? {mb: '16px', mt: '4px'}
    return (
        <Stack spacing={0} {...stackSpacing}>
            <Text
                fontSize="xs"
                lineHeight="1"
                mb="8px"
                color={hasInvalidRequirements ? 'webTypo.textError' : 'webTypo.textSecondary'}
            >
                <FormattedMessage
                    id="password_requirements.error.minimum_requirements"
                    defaultMessage="Password must have at least:"
                    description="Password requirement"
                />
            </Text>
            <PasswordRequirement isValid={pwValidations.hasLowercase}>
                <FormattedMessage
                    id="password_requirements.error.one_lowercase_letter"
                    defaultMessage="One lowercase character"
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasUppercase}>
                <FormattedMessage
                    id="password_requirements.error.one_uppercase_letter"
                    defaultMessage="One uppercase character"
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasNumber}>
                <FormattedMessage
                    defaultMessage="One number"
                    description="Password requirement"
                    id="password_requirements.error.one_number"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasSpecialChar}>
                <FormattedMessage
                    id="password_requirements.error.one_special_character"
                    defaultMessage="One special character"
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasMinChars}>
                <FormattedMessage
                    id="password_requirements.error.eight_letter_minimum"
                    defaultMessage="8 characters minimum"
                    description="Password requirement"
                />
            </PasswordRequirement>
        </Stack>
    )
}

PasswordRequirements.propTypes = {
    /** The password to check against */
    value: PropTypes.string,

    /** Caller identifier used to look up spacing overrides (e.g. 'confirmation') */
    source: PropTypes.string
}

export default PasswordRequirements
